import { capitalizeString } from '@strata/helper-functions';
import { GridWithSearch, SecondaryData } from '@strata/react-grid';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get } from 'services/Http';

const columns = {
    [UserType.SUPPLIER]: [
        { accessorKey: 'upn', header: 'User Id', size: 100 },
        { accessorKey: 'full_name', header: 'Name', size: 100 },
        {
            accessorKey: 'supplier_npi',
            header: 'Supplier NPI',
            size: 80,
            cell: ({ row }) => <SecondaryData data={row.original.pharmacies?.[0].supplier_npi} />,
        },
        {
            accessorKey: 'display_name',
            header: 'Role',
            size: 100,
            cell: ({ row }) => <SecondaryData data={row.original.pharmacies?.[0].display_name} />,
        },
    ],
    [UserType.PRESCRIBER]: [
        { accessorKey: 'upn', header: 'User Id', size: 100 },
        { accessorKey: 'prescriber_name', header: 'Name', size: 100 },
        { accessorKey: 'practice_npi', header: 'Practice NPI', size: 80 },
    ],
    [UserType.PAYER]: [
        { accessorKey: 'upn', header: 'User Id', size: 120 },
        { accessorKey: 'full_name', header: 'Name', size: 80 },
        { accessorKey: 'payer_id', header: 'Payer', size: 60 },
        {
            accessorKey: 'segment_ids',
            header: 'Payer Segment(s)',
            size: 150,
            cell: ({ getValue }) => <SecondaryData data={getValue()?.join(', ')} />,
        },
        { accessorKey: 'display_name', header: 'Role', size: 80 },
        {
            accessorKey: 'restricted_access',
            header: 'Restricted Referral Access',
            size: 90,
            cell: ({ getValue }) => <SecondaryData data={getValue() ? 'Yes' : 'No'} />,
        },
    ],
    [UserType.COMPASS]: [
        { accessorKey: 'upn', header: 'User Id', size: 120 },
        { accessorKey: 'full_name', header: 'Name', size: 80 },
        {
            accessorKey: 'segment_ids',
            header: 'Payer Segment(s)',
            size: 150,
            cell: ({ getValue }) => <SecondaryData data={getValue()?.join(', ')} />,
        },
        { accessorKey: 'display_name', header: 'Role', size: 80 },
    ],
    [UserType.LIGHTHOUSE]: [
        { accessorKey: 'upn', header: 'User Id', size: 120 },
        { accessorKey: 'full_name', header: 'Name', size: 80 },
        { accessorKey: 'display_name', header: 'Role', size: 80 },
    ],
};

export const UserManagement = () => {
    const { userType } = useParams();
    const navigate = useNavigate();

    const [users, setUsers] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setUsers(await get(`/authorization/${userType}_users/`));
            } catch (err) {
                handleError('Error fetching users.', err);
            }
        })();
        return () => {
            setUsers();
        };
    }, [userType]);

    return (
        <div className="sds_main-content__container">
            <ListPageHeader
                pageTitle={`${capitalizeString(userType)} Users`}
                buttons={[
                    {
                        label: `Create ${capitalizeString(userType)} User`,
                        path: `/user-management/${userType}/${CREATE_ROUTE}`,
                    },
                ]}
            />
            <div className="sds_main-content__grid">
                <GridWithSearch
                    thinRows
                    data={users}
                    isLoading={!users}
                    columns={columns[userType]}
                    sortProperties={[{ id: 'upn', desc: false }]}
                    onRowClick={(row) => navigate(`/user-management/${userType}/${row.upn}`)}
                />
            </div>
        </div>
    );
};
